import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

const StockLogo = ({ symbol }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
          {symbol.slice(0, 2)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={`https://financialmodelingprep.com/image-stock/${symbol}.png`}
      alt={`${symbol} logo`}
      className="w-8 h-8 rounded-full object-contain"
      onError={handleError}
    />
  );
};

const Gainers = () => {
  const [gainers, setGainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: 'change',
    direction: 'desc'
  });

  const itemsPerPage = 15;
  const title = "En Çok Kazanan Global Hisseler | Smart Portfoy";
  const description = "Dünya borsalarında en çok kazandıran hisseleri anlık olarak takip edin. Güncel fiyatlar, değişim oranları ve detaylı analizler.";

  useEffect(() => {
    const fetchGainers = async () => {
      try {
        const apiKey = process.env.REACT_APP_FMP_API_KEY;
        if (!apiKey) {
          throw new Error('API key is not configured');
        }

        const response = await fetch(
          `https://financialmodelingprep.com/api/v3/stock_market/gainers?apikey=${apiKey}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const data = await response.json();
        setGainers(data);
      } catch (error) {
        console.error('Error fetching gainers:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGainers();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'asc'
            ? 'desc'
            : 'asc'
          : 'desc',
    }));
  };

  const getSortedData = () => {
    if (!sortConfig.key) return gainers;

    return [...gainers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === 'string' && !isNaN(parseFloat(aValue))) {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <div className="w-4" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const paginatedData = getSortedData().slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(gainers.length / itemsPerPage);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-center">
          <p className="dark:text-gray-300">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center text-red-600 dark:text-red-400">
          <p>Hata: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href="https://smart-portfoy.com/piyasalar/en-cok-kazananlar" />
        <meta name="keywords" content="borsa, hisse senedi, en çok kazandıranlar, piyasa analizi, yatırım fırsatları" />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-purple-900 py-24">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              En Çok Kazanan Hisseler
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Global piyasalarda en iyi performans gösteren hisseleri takip edin. Anlık fiyatlar ve değişim oranlarıyla yatırım fırsatlarını kaçırmayın.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-32">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/market/market-banner.jpg" 
            alt="Market Gainers Banner"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700">
                  <th 
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                    onClick={() => handleSort('symbol')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Sembol</span>
                      {getSortIcon('symbol')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>İsim</span>
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Fiyat</span>
                      {getSortIcon('price')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                    onClick={() => handleSort('change')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Değişim</span>
                      {getSortIcon('change')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer"
                    onClick={() => handleSort('changesPercentage')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Değişim %</span>
                      {getSortIcon('changesPercentage')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedData.map((stock) => (
                  <tr key={stock.symbol} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <StockLogo symbol={stock.symbol} />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {stock.symbol}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {stock.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-gray-100">
                      ${Number(stock.price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-green-600 dark:text-green-400">
                      +${Number(stock.change).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-green-600 dark:text-green-400">
                      +{Number(stock.changesPercentage).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Önceki
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Sayfa {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
                >
                  Sonraki
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Gainers;